import { type KeyboardEvent, type ReactNode, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useDebounce } from '@/hooks/useDebounce';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Search, ExternalLink, ChevronDown, Container as ContainerIcon, MoreHorizontal, ArrowRightLeft, X, Archive, ArchiveRestore, Landmark } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { AssignPartnerSheet } from './AssignPartnerSheet';
import ContainerCustomsSheet from './ContainerCustomsSheet';
import { VehiclePipelineStages, type PipelineStage } from './VehiclePipeline';
import {
    type VehicleStage,
    PROCUREMENT_STAGE_KEYS,
    PROCUREMENT_STAGE_CONFIG,
    STAGE_BADGE_STYLES,
    STAGE_LABELS,
    computeVehicleStage,
    fetchVehicleContractsMap,
} from './vehicleStages';
import { VehicleCifDisplay, VehicleValueDisplay, CifBalanceFigures, sumCifPaid } from './VehicleFinancialCells';

// ─── Constants ──────────────────────────────────────────────────────────────

/** A physical container (SBT import batch) holds between 4 and 7 vehicles — used to accept/reject cluster splits and to flag an unsplittable oversized group. */
export const MIN_CLUSTER_SIZE = 4;
export const MAX_CONTAINER_SIZE = 7;

// ─── Types ──────────────────────────────────────────────────────────────────

export type PlugFilter = 'all' | '3-plug' | '4-plug';

interface RawVehicleProcurement {
    cif: number | null;
    est_arrival_at: string | null;
    ship_date: string | null;
    ship_name: string | null;
    voyage_no: string | null;
    bl_tracking_no: string | null;
    listing_url: string | null;
    /** SBT import batch timestamp — NOT NULL in the DB. Used as the primary cluster-splitting key. */
    created_at: string;
    /** Fallback cluster-splitting key when created_at doesn't yield valid container-sized clusters. */
    invoice_no: string | null;
}

interface RawVehicleTransaction {
    transaction_type: string;
    /** Per-vehicle share of a fanned-out transaction (e.g. a container-level GRA tax bill split
     * across N vehicles) — null for the (still-common) unsplit single-vehicle assignment, in which
     * case callers fall back to the full `transactions.amount`/`fee`. */
    allocated_amount: number | null;
    allocated_fee: number | null;
    transactions: { amount: number; fee: number; currency: string } | null;
}

/** A physical container's full (unfiltered) member vehicle, keyed by stable vehicle id — used for
 * customs queries/parsing, which must survive a recluster (BL backfill) that a derived cluster key
 * would not. */
export interface ContainerMember {
    id: number;
    vin: string;
    make: string;
    model: string;
}

interface RawProcurementVehicle {
    id: number;
    make: string;
    model: string;
    vin: string;
    color: string;
    engine_size: number | null;
    manufactured_at: number | null;
    updated_at: string;
    partner_id: number | null;
    archived_at: string | null;
    vehicle_procurement: RawVehicleProcurement | null;
    vehicle_registrations: { number: string } | null;
    vehicle_transactions: RawVehicleTransaction[] | null;
    partners: { name: string } | null;
}

export interface EnrichedVehicle extends RawProcurementVehicle {
    stage: VehicleStage;
}

/** CIF / paid / outstanding, whatever the source population — a full cluster or a filtered subset. */
export interface CifTotals {
    cif: number;
    cifPaid: number;
    outstanding: number;
}

export interface ContainerGroup {
    /** Unique per-cluster key — `${voyageKey}::${containerIndex}`. */
    key: string;
    /** The ship+voyage key this cluster's voyage belongs to (siblings share this — used to keep them adjacent when sorting). */
    voyageKey: string;
    /** 0-based position of this cluster among its voyage's clusters, ordered by min created_at then min invoice_no. */
    containerIndex: number;
    shipName: string | null;
    voyageNo: string | null;
    shipDate: string | null;
    estArrivalAt: string | null;
    /** e.g. ' · Container 2' when the voyage split into more than one tile, else null. */
    containerSuffix: string | null;
    /** Full (unfiltered) cluster size — the "m" in "n of m vehicles"; the physical container's true size. */
    fullSize: number;
    /** bl_tracking_no confirmed across the FULL (unfiltered) cluster, or null if not yet confirmed. */
    confirmedBl: string | null;
    /** CIF/paid/outstanding aggregated across the FULL (unfiltered) cluster — stable under filters. */
    fullTotals: CifTotals;
    /** Full (unfiltered) cluster membership, keyed by stable vehicle id — used by the Customs sheet
     * (never the derived cluster `key`, which a BL-backfill recluster can change). */
    fullMembers: ContainerMember[];
    /** The currently filtered/displayed subset of the cluster's vehicles (drives the expanded table body). */
    vehicles: EnrichedVehicle[];
}

/** Per-cluster display metadata + full (unfiltered) size/BL/totals, keyed by cluster key. */
export interface FullClusterInfo {
    key: string;
    voyageKey: string;
    containerIndex: number;
    shipName: string | null;
    voyageNo: string | null;
    shipDate: string | null;
    estArrivalAt: string | null;
    containerSuffix: string | null;
    fullSize: number;
    confirmedBl: string | null;
    fullTotals: CifTotals;
    /** Full (unfiltered) cluster membership, keyed by stable vehicle id — see `ContainerGroup.fullMembers`. */
    fullMembers: ContainerMember[];
}

/**
 * The full (unfiltered) partition of every vehicle into clusters, plus a lookup from vehicle id to the
 * cluster it belongs to. Computed once from the complete dataset so that filtering (search/stage/plug)
 * never changes container boundaries — it only changes which of a cluster's members are on-screen.
 */
export interface FullClusterIndex {
    clusterInfoByKey: Map<string, FullClusterInfo>;
    clusterKeyByVehicleId: Map<number, string>;
}

// ─── Pure helpers (unit-tested — see __tests__/procurementContainers.test.ts) ──

export function getStockId(listingUrl: string | null | undefined): string | null {
    if (!listingUrl) return null;
    const stockId = listingUrl.replace(/\/+$/, '').split('/').pop();
    return stockId || null;
}

export function passesPlugFilter(engineSize: number | null | undefined, filter: PlugFilter): boolean {
    if (filter === 'all') return true;
    if (!engineSize || engineSize <= 0) return false;
    if (filter === '3-plug') return engineSize <= 1000;
    return engineSize > 1000; // '4-plug'
}

export function vehicleMatchesSearch(vehicle: EnrichedVehicle, query: string): boolean {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const stockId = getStockId(vehicle.vehicle_procurement?.listing_url);
    return (
        vehicle.make.toLowerCase().includes(q) ||
        vehicle.model.toLowerCase().includes(q) ||
        vehicle.vin.toLowerCase().includes(q) ||
        vehicle.color.toLowerCase().includes(q) ||
        (vehicle.vehicle_registrations?.number?.toLowerCase().includes(q) ?? false) ||
        (stockId?.toLowerCase().includes(q) ?? false)
    );
}

function compareShipDateDesc(a: string | null, b: string | null): number {
    if (a === b) return 0;
    if (a === null) return 1; // nulls last
    if (b === null) return -1;
    return new Date(b).getTime() - new Date(a).getTime();
}

/**
 * Grouping key for a vehicle's procurement record — pure ship_name + voyage_no, JSON-encoded as
 * `["A ONTAKE","2625W"]` rather than delimiter-joined: ship_name/voyage_no are free text from PDF
 * import and could themselves contain "|", which would silently collide two distinct voyages into one
 * key. bl_tracking_no and ship_date play no part in the key (ship_date is display/sort only). Returns
 * null when neither ship_name nor voyage_no is recorded (no shipping info yet).
 */
export function computeGroupKey(proc: RawVehicleProcurement | null | undefined): string | null {
    const shipName = proc?.ship_name ?? null;
    const voyageNo = proc?.voyage_no ?? null;
    if (!shipName && !voyageNo) return null;
    return JSON.stringify([shipName, voyageNo]);
}

/** ceil(fullGroupSize / 6) — rough physical-container estimate for a ship+voyage group. */
export function approxContainerCount(fullGroupSize: number): number {
    return Math.ceil(fullGroupSize / 6);
}

/**
 * Whether a tile's "≈N containers" chip should render: only for a confirmed-oversized cluster
 * (fullSize > MAX_CONTAINER_SIZE) that failed every split attempt AND has no confirmed BL. A confirmed
 * BL is ground truth for exactly one physical container, so showing "≈N containers" alongside it would
 * be self-contradictory.
 */
export function shouldShowApproxChip(fullSize: number, confirmedBl: string | null): boolean {
    return fullSize > MAX_CONTAINER_SIZE && !confirmedBl;
}

/**
 * Whether a vehicle's stage makes its container eligible for customs actions (Bill of Entry
 * documents / tax-bill & port-charges payments). Customs only becomes relevant once the CIF is
 * fully paid — i.e. Balance and beyond. Denylist of the pre-balance procurement stages rather
 * than an allowlist, so contract stages, sold, archived, and any future stage stay eligible.
 */
const PRE_BALANCE_STAGES: ReadonlySet<VehicleStage> = new Set(['reserved', 'deposit', 'booked', 'shipped', 'balance_due']);
export function isCustomsEligibleStage(stage: VehicleStage): boolean {
    return !PRE_BALANCE_STAGES.has(stage);
}

/**
 * The muted "N progressed" hint appended to a tile's count label, or null when it shouldn't show.
 * Only shown when NO filters are active and the cluster's full (any-stage) membership outnumbers its
 * currently-visible (filtered, procurement-stage) membership — i.e. some of the physical container's
 * vehicles have left the procurement pipeline (contract signed, sold, ...). When filters ARE active,
 * a smaller visible count is explained by the filter itself, so no hint is shown.
 */
export function buildProgressedHint(visibleCount: number, fullSize: number, filtersActive: boolean): string | null {
    if (filtersActive) return null;
    const progressed = fullSize - visibleCount;
    return progressed > 0 ? `${progressed} progressed` : null;
}

/**
 * The bl_tracking_no confirmed for a cluster of vehicles, or null if not yet confirmed.
 * "Confirmed" means every single vehicle passed in shares the exact same non-null bl_tracking_no —
 * pass the FULL (unfiltered) membership of a cluster, not just the currently-displayed subset, since a
 * single unrecorded-BL row anywhere in the cluster should keep the whole tile at "BL pending".
 */
export function deriveGroupBl(vehicles: { vehicle_procurement: RawVehicleProcurement | null }[]): string | null {
    if (vehicles.length === 0) return null;
    const first = vehicles[0].vehicle_procurement?.bl_tracking_no ?? null;
    if (!first) return null;
    return vehicles.every(v => (v.vehicle_procurement?.bl_tracking_no ?? null) === first) ? first : null;
}

/**
 * Attempts to split `vehicles` by the value `keyFn` extracts from each. Rejects (returns null) unless
 * EVERY vehicle yields a non-null key AND every resulting sub-cluster's size falls in [4, 7] — a
 * partition that fails either condition tells the caller nothing usable about physical containers.
 */
function trySplitByKey<T>(vehicles: T[], keyFn: (v: T) => string | null): T[][] | null {
    const byKey = new Map<string, T[]>();
    for (const v of vehicles) {
        const k = keyFn(v);
        if (k === null) return null;
        const existing = byKey.get(k);
        if (existing) existing.push(v);
        else byKey.set(k, [v]);
    }
    const clusters = Array.from(byKey.values());
    return clusters.every(c => c.length >= MIN_CLUSTER_SIZE && c.length <= MAX_CONTAINER_SIZE) ? clusters : null;
}

/**
 * Splits an oversized (>MAX_CONTAINER_SIZE), BL-less vehicle cluster into physical-container-sized
 * sub-clusters by trying created_at then invoice_no, accepting the first that yields a valid
 * [MIN_CLUSTER_SIZE, MAX_CONTAINER_SIZE]-sized partition. Clusters at or under MAX_CONTAINER_SIZE are
 * returned whole — never sub-split (step 3 of the spec).
 */
function splitOversizedCluster<T extends { vehicle_procurement: RawVehicleProcurement | null }>(vehicles: T[]): T[][] {
    if (vehicles.length <= MAX_CONTAINER_SIZE) return [vehicles];
    const byCreatedAt = trySplitByKey(vehicles, v => v.vehicle_procurement?.created_at ?? null);
    if (byCreatedAt) return byCreatedAt;
    const byInvoiceNo = trySplitByKey(vehicles, v => v.vehicle_procurement?.invoice_no ?? null);
    if (byInvoiceNo) return byInvoiceNo;
    return [vehicles];
}

/** [min created_at, min invoice_no] across a cluster — the sort key used to number "Container 1..k". */
function clusterSortKey<T extends { vehicle_procurement: RawVehicleProcurement | null }>(cluster: T[]): [string, string] {
    let minCreatedAt = '';
    let minInvoiceNo = '';
    cluster.forEach(v => {
        const createdAt = v.vehicle_procurement?.created_at ?? '';
        const invoiceNo = v.vehicle_procurement?.invoice_no ?? '';
        if (createdAt && (!minCreatedAt || createdAt < minCreatedAt)) minCreatedAt = createdAt;
        if (invoiceNo && (!minInvoiceNo || invoiceNo < minInvoiceNo)) minInvoiceNo = invoiceNo;
    });
    return [minCreatedAt, minInvoiceNo];
}

/**
 * Splits ONE ship+voyage group's full (unfiltered) membership into physical-container-sized clusters:
 *  1. If any member has a bl_tracking_no, split by exact BL match — one cluster per distinct BL (a BL
 *     is ground truth for one physical container, so BL clusters are never further sub-split regardless
 *     of size). Members without a BL in such a group form a single remainder cluster.
 *  2. That remainder (or the whole group, when nothing has a BL) is sub-split via `splitOversizedCluster`
 *     when its size is > 7; groups of size <= 7 are never sub-split.
 * Returns clusters ordered by (min created_at, then min invoice_no) ascending — the order "Container
 * 1..k" should be numbered in.
 */
export function partitionGroup<T extends { vehicle_procurement: RawVehicleProcurement | null }>(vehicles: T[]): T[][] {
    const withBl: T[] = [];
    const withoutBl: T[] = [];
    vehicles.forEach(v => {
        if (v.vehicle_procurement?.bl_tracking_no) withBl.push(v);
        else withoutBl.push(v);
    });

    let clusters: T[][];
    if (withBl.length > 0) {
        const byBl = new Map<string, T[]>();
        withBl.forEach(v => {
            const bl = v.vehicle_procurement?.bl_tracking_no as string;
            const existing = byBl.get(bl);
            if (existing) existing.push(v);
            else byBl.set(bl, [v]);
        });
        clusters = Array.from(byBl.values());
        if (withoutBl.length > 0) clusters.push(...splitOversizedCluster(withoutBl));
    } else {
        clusters = splitOversizedCluster(withoutBl);
    }

    return clusters.sort((a, b) => {
        const [aCreatedAt, aInvoiceNo] = clusterSortKey(a);
        const [bCreatedAt, bInvoiceNo] = clusterSortKey(b);
        if (aCreatedAt !== bCreatedAt) return aCreatedAt < bCreatedAt ? -1 : 1;
        if (aInvoiceNo !== bInvoiceNo) return aInvoiceNo < bInvoiceNo ? -1 : 1;
        return 0;
    });
}

/**
 * Buckets ALL vehicles (any stage) by ship+voyage key, partitions each voyage's full membership into
 * clusters via `partitionGroup`, and returns per-cluster display metadata plus a vehicle-id → cluster-key
 * lookup. Computed once from the complete dataset so that filtering a subset of these same vehicles can
 * never change container boundaries — see `FullClusterIndex`.
 */
export function buildFullClusterIndex<T extends { id: number; vin: string; make: string; model: string; vehicle_procurement: RawVehicleProcurement | null; vehicle_transactions: RawVehicleTransaction[] | null }>(
    vehicles: T[],
): FullClusterIndex {
    const voyageBuckets = new Map<string, T[]>();
    vehicles.forEach(v => {
        const voyageKey = computeGroupKey(v.vehicle_procurement);
        if (!voyageKey) return;
        const existing = voyageBuckets.get(voyageKey);
        if (existing) existing.push(v);
        else voyageBuckets.set(voyageKey, [v]);
    });

    const clusterInfoByKey = new Map<string, FullClusterInfo>();
    const clusterKeyByVehicleId = new Map<number, string>();

    voyageBuckets.forEach((members, voyageKey) => {
        const clusters = partitionGroup(members);
        clusters.forEach((clusterVehicles, containerIndex) => {
            const key = `${voyageKey}::${containerIndex}`;
            const proc = clusterVehicles[0].vehicle_procurement;
            clusterInfoByKey.set(key, {
                key,
                voyageKey,
                containerIndex,
                shipName: proc?.ship_name ?? null,
                voyageNo: proc?.voyage_no ?? null,
                shipDate: proc?.ship_date ?? null,
                estArrivalAt: proc?.est_arrival_at ?? null,
                containerSuffix: clusters.length > 1 ? ` · Container ${containerIndex + 1}` : null,
                fullSize: clusterVehicles.length,
                confirmedBl: deriveGroupBl(clusterVehicles),
                fullTotals: containerTotals(clusterVehicles),
                fullMembers: clusterVehicles.map(v => ({ id: v.id, vin: v.vin, make: v.make, model: v.model })),
            });
            clusterVehicles.forEach(v => clusterKeyByVehicleId.set(v.id, key));
        });
    });

    return { clusterInfoByKey, clusterKeyByVehicleId };
}

/**
 * Groups `vehicles` by their precomputed cluster key (from `clusterIndex`, built over the full unfiltered
 * dataset — see `buildFullClusterIndex`). Vehicles with no shipping info at all (absent from the index)
 * go to `notYetShipped`. Groups are sorted ship_date desc/nulls-last, keeping a voyage's sibling clusters
 * adjacent and in Container 1..k order; `notYetShipped` is sorted the same way.
 */
export function groupVehicles(
    vehicles: EnrichedVehicle[],
    clusterIndex: FullClusterIndex,
): { groups: ContainerGroup[]; notYetShipped: EnrichedVehicle[] } {
    const byClusterKey = new Map<string, EnrichedVehicle[]>();
    const notYetShipped: EnrichedVehicle[] = [];

    vehicles.forEach(v => {
        const clusterKey = clusterIndex.clusterKeyByVehicleId.get(v.id);
        if (!clusterKey) {
            notYetShipped.push(v);
            return;
        }
        const existing = byClusterKey.get(clusterKey);
        if (existing) existing.push(v);
        else byClusterKey.set(clusterKey, [v]);
    });

    const groups: ContainerGroup[] = Array.from(byClusterKey.entries()).map(([key, vs]) => {
        const info = clusterIndex.clusterInfoByKey.get(key);
        // clusterIndex was built from the superset that produced `vehicles`, so info is always present.
        // fullSize/confirmedBl/fullTotals are threaded through here (from the FULL, unfiltered cluster)
        // so the tile can read them straight off its group — no second clusterIndex lookup at render time.
        return {
            key,
            voyageKey: info?.voyageKey ?? key,
            containerIndex: info?.containerIndex ?? 0,
            shipName: info?.shipName ?? null,
            voyageNo: info?.voyageNo ?? null,
            shipDate: info?.shipDate ?? null,
            estArrivalAt: info?.estArrivalAt ?? null,
            containerSuffix: info?.containerSuffix ?? null,
            fullSize: info?.fullSize ?? vs.length,
            confirmedBl: info?.confirmedBl ?? null,
            fullTotals: info?.fullTotals ?? containerTotals(vs),
            fullMembers: info?.fullMembers ?? vs.map(v => ({ id: v.id, vin: v.vin, make: v.make, model: v.model })),
            vehicles: vs,
        };
    });

    groups.sort((a, b) => {
        const shipCmp = compareShipDateDesc(a.shipDate, b.shipDate);
        if (shipCmp !== 0) return shipCmp;
        if (a.voyageKey !== b.voyageKey) return a.voyageKey < b.voyageKey ? -1 : 1;
        return a.containerIndex - b.containerIndex;
    });
    notYetShipped.sort((a, b) => compareShipDateDesc(a.vehicle_procurement?.ship_date ?? null, b.vehicle_procurement?.ship_date ?? null));

    return { groups, notYetShipped };
}

/** CIF / paid / outstanding aggregation for a set of vehicles — same per-vehicle formula as VehicleCifDisplay. */
export function containerTotals(vehicles: { vehicle_procurement: RawVehicleProcurement | null; vehicle_transactions: RawVehicleTransaction[] | null }[]): CifTotals {
    let cif = 0;
    let cifPaid = 0;
    vehicles.forEach(v => {
        cif += v.vehicle_procurement?.cif ?? 0;
        cifPaid += sumCifPaid(v.vehicle_transactions);
    });
    return { cif, cifPaid, outstanding: cif - cifPaid };
}

/** Distinct stages present in a container, sorted by frequency desc (ties broken by pipeline order). */
export function dominantStages(vehicles: EnrichedVehicle[]): { stage: VehicleStage; count: number }[] {
    const counts = new Map<VehicleStage, number>();
    vehicles.forEach(v => counts.set(v.stage, (counts.get(v.stage) ?? 0) + 1));
    return Array.from(counts.entries())
        .map(([stage, count]) => ({ stage, count }))
        .sort((a, b) => {
            if (b.count !== a.count) return b.count - a.count;
            return PROCUREMENT_STAGE_KEYS.indexOf(a.stage) - PROCUREMENT_STAGE_KEYS.indexOf(b.stage);
        });
}

// ─── Small display helpers ──────────────────────────────────────────────────

/**
 * ship_date/est_arrival_at are Postgres DATE strings (e.g. "2026-06-01"), which `new Date(...)` parses
 * as UTC midnight. Formatting with the viewer's LOCAL timezone then rolls negative-UTC-offset viewers
 * back a day (2026-06-01 renders as "31 May 2026"). Force `timeZone: 'UTC'` so the date renders exactly
 * as stored, everywhere.
 */
export function formatShortDate(d: string | null | undefined): string | null {
    if (!d) return null;
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function buildTileTitle(c: ContainerGroup): string {
    const base = [c.shipName, c.voyageNo ? `V.${c.voyageNo}` : null].filter(Boolean).join(' · ');
    return c.containerSuffix ? `${base}${c.containerSuffix}` : base;
}

function buildTileSubtitle(c: ContainerGroup): string {
    const shipped = formatShortDate(c.shipDate);
    const eta = formatShortDate(c.estArrivalAt);
    if (shipped && eta) return `Shipped ${shipped} → ETA ${eta}`;
    if (shipped) return `Shipped ${shipped}`;
    if (eta) return `ETA ${eta}`;
    return '';
}

function getSbtPhotoUrl(listingUrl: string | null | undefined, updatedAt: string | null | undefined): string | null {
    if (!listingUrl) return null;
    const match = listingUrl.match(/sbtjapan\.com\/used-cars\/([A-Z0-9]+)/i);
    if (!match) return null;
    const cacheBust = updatedAt ? `?v=${new Date(updatedAt).getTime()}` : '';
    return `https://img.sbtjapan.com/img/carphoto/${match[1]}/normal/f.jpg${cacheBust}`;
}

function getColorValue(color: string): string {
    const trimmed = color.trim();
    if (/^[0-9A-Fa-f]{6}$/.test(trimmed)) return `#${trimmed}`;
    return trimmed;
}

/** "Make Model · stockId" — same label format AssignPartnerSheet expects (mirrors VehiclesRecords' getVehicleLabel). */
function getVehicleLabel(vehicle: EnrichedVehicle): string {
    const name = `${vehicle.make} ${vehicle.model}`;
    const stockId = getStockId(vehicle.vehicle_procurement?.listing_url);
    return stockId ? `${name} · ${stockId}` : name;
}

// ─── Shared table cells (tile body + not-yet-shipped table) ────────────────

function VehicleCell({ vehicle }: { vehicle: EnrichedVehicle }) {
    const listingUrl = vehicle.vehicle_procurement?.listing_url || '';
    const photoUrl = getSbtPhotoUrl(listingUrl, vehicle.updated_at);
    return (
        <div className="flex items-center gap-3">
            {photoUrl ? (
                <HoverCard openDelay={200} closeDelay={100}>
                    <HoverCardTrigger asChild>
                        <img src={photoUrl} alt="" className="h-9 w-9 rounded-lg border border-gray-200 object-cover flex-shrink-0 cursor-zoom-in" />
                    </HoverCardTrigger>
                    <HoverCardContent side="right" align="center" className="w-auto p-1" sideOffset={8}>
                        <img src={photoUrl} alt={`${vehicle.make} ${vehicle.model}`} className="w-[480px] rounded-md" />
                    </HoverCardContent>
                </HoverCard>
            ) : (
                <div className="h-9 w-9 rounded-lg border border-gray-200 flex-shrink-0" style={{ backgroundColor: getColorValue(vehicle.color) || '#e5e7eb' }} />
            )}
            <div>
                <p className="font-medium">{vehicle.make} {vehicle.model}</p>
                <p className="text-xs text-muted-foreground">
                    {vehicle.manufactured_at || ''}
                    {vehicle.engine_size ? `${vehicle.manufactured_at ? ' · ' : ''}${vehicle.engine_size}cc` : ''}
                </p>
            </div>
            {listingUrl && (
                <a
                    href={listingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    className="ml-auto text-muted-foreground/50 hover:text-primary transition-colors"
                    title="View listing"
                >
                    <ExternalLink className="h-3.5 w-3.5" />
                </a>
            )}
        </div>
    );
}

function IdentificationCell({ vehicle }: { vehicle: EnrichedVehicle }) {
    const stockId = getStockId(vehicle.vehicle_procurement?.listing_url);
    return (
        <div>
            {vehicle.vehicle_registrations?.number && <p className="font-medium">{vehicle.vehicle_registrations.number}</p>}
            <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{vehicle.vin}</code>
            {stockId && <p className="text-xs text-muted-foreground mt-0.5">{stockId}</p>}
        </div>
    );
}

function PartnerCell({ vehicle }: { vehicle: EnrichedVehicle }) {
    return vehicle.partners?.name
        ? <span className="text-sm">{vehicle.partners.name}</span>
        : <span className="text-muted-foreground">—</span>;
}

function StatusCell({ vehicle }: { vehicle: EnrichedVehicle }) {
    return <Badge variant="outline" className={STAGE_BADGE_STYLES[vehicle.stage]}>{STAGE_LABELS[vehicle.stage]}</Badge>;
}

/** The six-cell Vehicle/Identification/Partner/Status/CIF/Value row shared by the tile's expanded body and the not-yet-shipped table. */
/** Procurement-stage row actions: assign/reassign/unassign a partner, and archive/unarchive. No Sell/Edit Sale — those are contract-stage concerns handled only in the List view. */
interface VehicleActionHandlers {
    onAssignPartner: (vehicle: EnrichedVehicle) => void;
    onReassignPartner: (vehicle: EnrichedVehicle) => void;
    onUnassignPartner: (vehicle: EnrichedVehicle) => void;
    onArchiveVehicle: (vehicle: EnrichedVehicle) => void;
    onUnarchiveVehicle: (vehicle: EnrichedVehicle) => void;
}

function VehicleTableRow({ vehicle, onRowClick, onAssignPartner, onReassignPartner, onUnassignPartner, onArchiveVehicle, onUnarchiveVehicle }: { vehicle: EnrichedVehicle; onRowClick: (vehicleId: number) => void } & VehicleActionHandlers) {
    const isArchived = !!vehicle.archived_at;
    const archiveMenuItem = isArchived ? (
        <DropdownMenuItem onClick={() => onUnarchiveVehicle(vehicle)}>
            <ArchiveRestore className="h-4 w-4 mr-2" />
            Unarchive
        </DropdownMenuItem>
    ) : (
        <DropdownMenuItem onClick={() => onArchiveVehicle(vehicle)}>
            <Archive className="h-4 w-4 mr-2" />
            Archive
        </DropdownMenuItem>
    );

    return (
        <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => onRowClick(vehicle.id)}>
            <TableCell><VehicleCell vehicle={vehicle} /></TableCell>
            <TableCell><IdentificationCell vehicle={vehicle} /></TableCell>
            <TableCell><PartnerCell vehicle={vehicle} /></TableCell>
            <TableCell><StatusCell vehicle={vehicle} /></TableCell>
            <TableCell><VehicleCifDisplay cif={vehicle.vehicle_procurement?.cif} vehicleTransactions={vehicle.vehicle_transactions} /></TableCell>
            <TableCell className="text-right font-medium"><VehicleValueDisplay vehicleTransactions={vehicle.vehicle_transactions} /></TableCell>
            <TableCell onClick={(e) => e.stopPropagation()}>
                {!vehicle.partner_id ? (
                    <div className="flex items-center justify-end gap-1">
                        <Button variant="outline" size="sm" onClick={() => onAssignPartner(vehicle)}>
                            Assign
                        </Button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <MoreHorizontal className="h-4 w-4" />
                                    <span className="sr-only">Actions</span>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                {archiveMenuItem}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                ) : (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                                <span className="sr-only">Actions</span>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => onReassignPartner(vehicle)}>
                                <ArrowRightLeft className="h-4 w-4 mr-2" />
                                Reassign
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() => onUnassignPartner(vehicle)}
                                className="text-destructive focus:text-destructive"
                            >
                                <X className="h-4 w-4 mr-2" />
                                Unassign
                            </DropdownMenuItem>
                            {archiveMenuItem}
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </TableCell>
        </TableRow>
    );
}

// ─── Container thumbnails ────────────────────────────────────────────────────

function ContainerThumbnails({ vehicles }: { vehicles: EnrichedVehicle[] }) {
    return (
        <div className="flex items-center -space-x-2 shrink-0">
            {vehicles.map(v => {
                const photoUrl = getSbtPhotoUrl(v.vehicle_procurement?.listing_url, v.updated_at);
                return photoUrl ? (
                    <HoverCard key={v.id} openDelay={200} closeDelay={100}>
                        <HoverCardTrigger asChild>
                            <img
                                src={photoUrl}
                                alt=""
                                className="h-7 w-7 rounded-full border-2 border-background object-cover cursor-zoom-in"
                            />
                        </HoverCardTrigger>
                        <HoverCardContent side="right" align="center" className="w-auto p-1" sideOffset={8}>
                            <img src={photoUrl} alt={`${v.make} ${v.model}`} className="w-[480px] rounded-md" />
                        </HoverCardContent>
                    </HoverCard>
                ) : (
                    <div
                        key={v.id}
                        className="h-7 w-7 rounded-full border-2 border-background"
                        style={{ backgroundColor: getColorValue(v.color) || '#e5e7eb' }}
                    />
                );
            })}
        </div>
    );
}

// ─── Container tile ──────────────────────────────────────────────────────────

interface ContainerTileProps extends VehicleActionHandlers {
    container: ContainerGroup;
    /** Whether search/stage/plug filters are currently active — gates the "N progressed" count hint. */
    filtersActive: boolean;
    isExpanded: boolean;
    onToggle: () => void;
    onRowClick: (vehicleId: number) => void;
    onOpenCustoms: (container: ContainerGroup) => void;
    /** Customs actions apply only from Balance onward — see isCustomsEligibleStage. */
    customsEligible: boolean;
}

function ContainerTile({ container, filtersActive, isExpanded, onToggle, onRowClick, onAssignPartner, onReassignPartner, onUnassignPartner, onArchiveVehicle, onUnarchiveVehicle, onOpenCustoms, customsEligible }: ContainerTileProps) {
    // Header figures (CIF/Bal, count denominator, BL chip, ≈N flag) all come from the FULL cluster —
    // container.fullSize/confirmedBl/fullTotals — so the header describes the physical container and
    // stays stable under filters. Only the expanded body below reflects the filtered/visible subset.
    const { fullSize, confirmedBl, fullTotals } = container;
    const stages = dominantStages(container.vehicles);
    const title = buildTileTitle(container);
    const subtitle = buildTileSubtitle(container);
    const showApproxChip = shouldShowApproxChip(fullSize, confirmedBl);
    const approxContainers = approxContainerCount(fullSize);
    const n = container.vehicles.length;
    const countLabel = n === fullSize ? `${n} vehicles` : `${n} of ${fullSize} vehicles`;
    const progressedHint = buildProgressedHint(n, fullSize, filtersActive);

    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
        }
    };

    return (
        <Card className="overflow-hidden">
            <div
                role="button"
                tabIndex={0}
                aria-expanded={isExpanded}
                onClick={onToggle}
                onKeyDown={handleKeyDown}
                className="flex flex-wrap items-center gap-4 px-4 py-3 cursor-pointer hover:bg-muted/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
            >
                {/* Left: glyph + title/subtitle */}
                <div className="flex items-center gap-3 min-w-[220px]">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
                        <ContainerIcon className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="font-medium truncate">{title}</p>
                            <Badge variant="secondary" className="text-[10px] font-normal text-muted-foreground">
                                {confirmedBl ? `BL ${confirmedBl}` : 'BL pending'}
                            </Badge>
                            {showApproxChip && (
                                <Badge variant="secondary" className="text-[10px] font-normal text-muted-foreground">≈{approxContainers} containers</Badge>
                            )}
                        </div>
                        {subtitle && <p className="text-xs text-muted-foreground truncate">{subtitle}</p>}
                    </div>
                </div>

                {/* Middle: thumbnails + count */}
                <div className="flex flex-1 items-center gap-3 min-w-[160px]">
                    <ContainerThumbnails vehicles={container.vehicles} />
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                        {countLabel}
                        {progressedHint && <span className="text-muted-foreground/60"> · {progressedHint}</span>}
                    </span>
                </div>

                {/* Right: status badge(s) + CIF + chevron */}
                <div className="flex items-center gap-3 shrink-0 ml-auto">
                    <div className="flex items-center gap-1.5">
                        {stages.slice(0, 2).map(s => (
                            <Badge key={s.stage} variant="outline" className={STAGE_BADGE_STYLES[s.stage]}>
                                {STAGE_LABELS[s.stage]}{stages.length > 1 ? ` · ${s.count}` : ''}
                            </Badge>
                        ))}
                    </div>
                    <CifBalanceFigures cif={fullTotals.cif} outstanding={fullTotals.outstanding} align="right" />
                    {customsEligible && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            onClick={(e) => { e.stopPropagation(); onOpenCustoms(container); }}
                            onKeyDown={(e) => e.stopPropagation()}
                            title="Customs documents & payments"
                        >
                            <Landmark className="h-4 w-4 text-muted-foreground" />
                        </Button>
                    )}
                    <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', !isExpanded && '-rotate-90')} />
                </div>
            </div>

            {isExpanded && (
                <div className="border-t overflow-x-auto">
                    <Table>
                        <TableBody>
                            {container.vehicles.map(v => (
                                <VehicleTableRow
                                    key={v.id}
                                    vehicle={v}
                                    onRowClick={onRowClick}
                                    onAssignPartner={onAssignPartner}
                                    onReassignPartner={onReassignPartner}
                                    onUnassignPartner={onUnassignPartner}
                                    onArchiveVehicle={onArchiveVehicle}
                                    onUnarchiveVehicle={onUnarchiveVehicle}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </Card>
    );
}

// ─── Not yet shipped section ─────────────────────────────────────────────────

function NotYetShippedSection({ vehicles, onRowClick, onAssignPartner, onReassignPartner, onUnassignPartner, onArchiveVehicle, onUnarchiveVehicle }: { vehicles: EnrichedVehicle[]; onRowClick: (vehicleId: number) => void } & VehicleActionHandlers) {
    const [open, setOpen] = useState(true);

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div>
                    <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold">Not yet shipped</h3>
                        <Badge variant="secondary">{vehicles.length}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">Vehicles appear here until shipping details are recorded.</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-label={open ? 'Collapse' : 'Expand'}>
                    <ChevronDown className={cn('h-4 w-4 transition-transform', !open && '-rotate-90')} />
                </Button>
            </div>

            {open && (
                <Card>
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Vehicle</TableHead>
                                    <TableHead>Identification</TableHead>
                                    <TableHead>Partner</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>CIF</TableHead>
                                    <TableHead className="text-right">Value</TableHead>
                                    <TableHead className="w-12"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {vehicles.map(v => (
                                    <VehicleTableRow
                                        key={v.id}
                                        vehicle={v}
                                        onRowClick={onRowClick}
                                        onAssignPartner={onAssignPartner}
                                        onReassignPartner={onReassignPartner}
                                        onUnassignPartner={onUnassignPartner}
                                        onArchiveVehicle={onArchiveVehicle}
                                        onUnarchiveVehicle={onUnarchiveVehicle}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </Card>
            )}
        </div>
    );
}

// ─── Main component ─────────────────────────────────────────────────────────

interface ProcurementContainersProps {
    /** Rendered right-aligned in the search + plugs row (e.g. a view-toggle control from the parent page). */
    controlsExtra?: ReactNode;
}

export default function ProcurementContainers({ controlsExtra }: ProcurementContainersProps) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    const [plugFilter, setPlugFilter] = useState<PlugFilter>('all');
    const [stageFilter, setStageFilter] = useState<VehicleStage | null>(null);
    const [manualExpanded, setManualExpanded] = useState<Set<string>>(new Set());

    // ── Partner assignment state (mirrors VehiclesRecords' procurement-actions cell) ──
    const [assignPartnerOpen, setAssignPartnerOpen] = useState(false);
    const [partnerAssignTarget, setPartnerAssignTarget] = useState<{
        id: number;
        label: string;
        currentPartnerId: number | null;
    } | null>(null);
    const [unassignPartnerTarget, setUnassignPartnerTarget] = useState<{
        id: number;
        partnerName: string;
    } | null>(null);

    // Archive vehicle state (Unarchive is direct — no confirmation target needed)
    const [archiveTarget, setArchiveTarget] = useState<{ id: number; label: string } | null>(null);

    // Customs (Bill of Entry / Tax Bill / Port Charges) sheet target
    const [customsTarget, setCustomsTarget] = useState<ContainerGroup | null>(null);

    const { data: rawVehicles } = useQuery({
        queryKey: ['procurement-containers-vehicles'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('vehicles')
                .select('id, make, model, vin, color, engine_size, manufactured_at, updated_at, partner_id, archived_at, vehicle_procurement(cif, est_arrival_at, ship_date, ship_name, voyage_no, bl_tracking_no, listing_url, created_at, invoice_no), vehicle_registrations(number), vehicle_transactions(transaction_type, allocated_amount, allocated_fee, transactions(amount, fee, currency)), partners!vehicles_partner_id_fkey(name)')
                // Cluster correctness depends on seeing every procurement vehicle in one shot — PostgREST's
                // default 1000-row cap would silently truncate the fleet and corrupt cluster boundaries.
                .limit(5000);
            if (error) throw error;
            return data as unknown as RawProcurementVehicle[];
        },
    });

    const { data: contractMap } = useQuery({
        queryKey: ['vehicle-contracts-map'],
        queryFn: fetchVehicleContractsMap,
    });

    const { data: soldIds } = useQuery({
        queryKey: ['vehicle-sold-ids'],
        queryFn: async () => {
            const { data, error } = await supabase.from('vehicle_sales').select('vehicle_id');
            if (error) throw error;
            return new Set((data || []).map(r => r.vehicle_id as number));
        },
    });

    const isLoading = rawVehicles === undefined || contractMap === undefined || soldIds === undefined;

    // ── Full (unfiltered) cluster index — container/shipment boundaries, sizes, and BL confirmation ──
    // Built from ALL vehicles (any stage), so filtering below can never change container boundaries.
    const fullClusterIndex = useMemo(() => buildFullClusterIndex(rawVehicles ?? []), [rawVehicles]);

    // ── Stage per vehicle and pipeline counts ──
    const { procurementVehicles, stageCounts, groupCount, customsEligibleClusterKeys } = useMemo(() => {
        const procurement: EnrichedVehicle[] = [];
        const counts = new Map<VehicleStage, number>();
        let total = 0;
        // Clusters with at least one member at Balance or beyond (evaluated over the FULL,
        // unfiltered membership — incl. contract-stage/sold members — so active filters never
        // change which tiles offer the customs action).
        const eligibleClusterKeys = new Set<string>();

        if (rawVehicles && contractMap && soldIds) {
            rawVehicles.forEach(v => {
                let stage: VehicleStage | null;
                if (soldIds.has(v.id)) {
                    stage = 'sold';
                } else {
                    const txTypes = (v.vehicle_transactions || []).map(vt => vt.transaction_type);
                    const contractStatus = contractMap.statusMap.get(v.id);
                    const proc = v.vehicle_procurement;
                    const hasShippingDetails = !!(proc?.ship_name || proc?.voyage_no);
                    stage = computeVehicleStage(txTypes, contractStatus, proc?.est_arrival_at ?? null, proc?.ship_date ?? null, hasShippingDetails, v.archived_at);
                }

                if (stage && isCustomsEligibleStage(stage)) {
                    const clusterKey = fullClusterIndex.clusterKeyByVehicleId.get(v.id);
                    if (clusterKey) eligibleClusterKeys.add(clusterKey);
                }

                if (stage && PROCUREMENT_STAGE_KEYS.includes(stage)) {
                    procurement.push({ ...v, stage });
                }
            });

            procurement.forEach(v => {
                if (passesPlugFilter(v.engine_size, plugFilter)) {
                    counts.set(v.stage, (counts.get(v.stage) ?? 0) + 1);
                    total++;
                }
            });
        }

        return { procurementVehicles: procurement, stageCounts: counts, groupCount: total, customsEligibleClusterKeys: eligibleClusterKeys };
    }, [rawVehicles, contractMap, soldIds, plugFilter, fullClusterIndex]);

    // ── Apply plug + stage + search filters, then group by the precomputed (full-dataset) cluster key ──
    const { groups, notYetShipped } = useMemo(() => {
        const filtered = procurementVehicles
            .filter(v => passesPlugFilter(v.engine_size, plugFilter))
            .filter(v => stageFilter === null || v.stage === stageFilter)
            .filter(v => vehicleMatchesSearch(v, debouncedSearch));
        return groupVehicles(filtered, fullClusterIndex);
    }, [procurementVehicles, plugFilter, stageFilter, debouncedSearch, fullClusterIndex]);

    const procurementStages: PipelineStage[] = PROCUREMENT_STAGE_CONFIG.map(s => ({
        ...s, count: stageCounts.get(s.key) ?? 0,
    }));

    const searchActive = debouncedSearch.trim().length > 0;
    const toggleGroup = (key: string) => {
        setManualExpanded(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handleRowClick = (vehicleId: number) => navigate(`/dashboard/vehicles/${vehicleId}`);

    // ── Partner assignment helpers (mirrors VehiclesRecords' procurement/all actions cell) ──
    const handleAssignPartner = (vehicle: EnrichedVehicle) => {
        setPartnerAssignTarget({
            id: vehicle.id,
            label: getVehicleLabel(vehicle),
            currentPartnerId: vehicle.partner_id ?? null,
        });
        setAssignPartnerOpen(true);
    };

    const handleReassignPartner = (vehicle: EnrichedVehicle) => {
        handleAssignPartner(vehicle);
    };

    const handleUnassignPartner = (vehicle: EnrichedVehicle) => {
        setUnassignPartnerTarget({
            id: vehicle.id,
            partnerName: vehicle.partners?.name || 'Unknown',
        });
    };

    const unassignPartnerMutation = useMutation({
        mutationFn: async (vehicleId: number) => {
            const { error } = await supabase
                .from('vehicles')
                .update({ partner_id: null })
                .eq('id', vehicleId);
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success('Partner removed from vehicle');
            // This view's own cache, plus the List view's — an unassign from a container tile must stay
            // consistent if the user switches to the List view (and vice versa isn't possible from here,
            // but these are the same keys VehiclesRecords' unassign mutation invalidates).
            queryClient.invalidateQueries({ queryKey: ['procurement-containers-vehicles'] });
            queryClient.invalidateQueries({ queryKey: ['vehicles'] });
            queryClient.invalidateQueries({ queryKey: ['vehicles-count'] });
            queryClient.invalidateQueries({ queryKey: ['fleet-vehicles-stages'] });
            queryClient.invalidateQueries({ queryKey: ['partner-vehicles'] });
            queryClient.invalidateQueries({ queryKey: ['partner-vehicles-page'] });
            queryClient.invalidateQueries({ queryKey: ['partner-vehicle-stats'] });
            queryClient.invalidateQueries({ queryKey: ['partner-financial-summary'] });
            queryClient.invalidateQueries({ queryKey: ['partner-recent-activity'] });
            queryClient.invalidateQueries({ queryKey: ['partners'] });
            queryClient.invalidateQueries({ queryKey: ['unassigned-vehicles-for-partner'] });
            setUnassignPartnerTarget(null);
        },
        onError: (error: Error) => {
            toast.error(error.message);
        },
    });

    const handleConfirmUnassign = () => {
        if (unassignPartnerTarget) {
            unassignPartnerMutation.mutate(unassignPartnerTarget.id);
        }
    };

    const handlePartnerSheetClose = (open: boolean) => {
        setAssignPartnerOpen(open);
        if (!open) {
            setPartnerAssignTarget(null);
        }
    };

    // ── Archive vehicle helpers (mirrors VehiclesRecords') ──────────────────
    // Marks a vehicle as never reaching a contract (third-party sale, third-party procurement, or
    // internal Primelane use). Archive requires confirmation; Unarchive is its direct undo.

    const handleArchiveVehicle = (vehicle: EnrichedVehicle) => {
        setArchiveTarget({ id: vehicle.id, label: getVehicleLabel(vehicle) });
    };

    const handleUnarchiveVehicle = (vehicle: EnrichedVehicle) => {
        unarchiveVehicleMutation.mutate(vehicle.id);
    };

    const handleOpenCustoms = (container: ContainerGroup) => setCustomsTarget(container);

    const invalidateArchiveQueries = () => {
        queryClient.invalidateQueries({ queryKey: ['vehicles'] });
        queryClient.invalidateQueries({ queryKey: ['vehicles-count'] });
        queryClient.invalidateQueries({ queryKey: ['fleet-vehicles-stages'] });
        queryClient.invalidateQueries({ queryKey: ['procurement-containers-vehicles'] });
    };

    const archiveVehicleMutation = useMutation({
        mutationFn: async (vehicleId: number) => {
            const { error } = await supabase
                .from('vehicles')
                .update({ archived_at: new Date().toISOString() })
                .eq('id', vehicleId);
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success('Vehicle archived');
            invalidateArchiveQueries();
            setArchiveTarget(null);
        },
        onError: (error: Error) => {
            toast.error(error.message);
        },
    });

    const unarchiveVehicleMutation = useMutation({
        mutationFn: async (vehicleId: number) => {
            const { error } = await supabase
                .from('vehicles')
                .update({ archived_at: null })
                .eq('id', vehicleId);
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success('Vehicle unarchived');
            invalidateArchiveQueries();
        },
        onError: (error: Error) => {
            toast.error(error.message);
        },
    });

    const handleConfirmArchive = () => {
        if (archiveTarget) {
            archiveVehicleMutation.mutate(archiveTarget.id);
        }
    };

    const hasResults = groups.length > 0 || notYetShipped.length > 0;
    const filtersActive = debouncedSearch.trim() !== '' || stageFilter !== null || plugFilter !== 'all';

    return (
        <Card className="border-0">
            <CardContent className="space-y-4">
                {/* Search + plugs + view toggle + pipeline stepper */}
                <div className="space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="relative w-full sm:w-64">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input placeholder="Search vehicles..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
                        </div>
                        <Select value={plugFilter} onValueChange={(val) => setPlugFilter(val as PlugFilter)}>
                            <SelectTrigger className="w-[140px] shrink-0">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Plugs</SelectItem>
                                <SelectItem value="3-plug">3 Plugs</SelectItem>
                                <SelectItem value="4-plug">4 Plugs</SelectItem>
                            </SelectContent>
                        </Select>
                        {controlsExtra && <div className="ml-auto">{controlsExtra}</div>}
                    </div>
                    <VehiclePipelineStages
                        stages={procurementStages}
                        activeStage={stageFilter}
                        onStageClick={(key) => setStageFilter(key as VehicleStage | null)}
                        groupCount={groupCount}
                    />
                </div>

                {isLoading ? (
                    <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
                ) : !hasResults ? (
                    <div className="py-16 text-center text-muted-foreground">
                        {filtersActive ? 'No vehicles match your filters' : 'No vehicles in procurement'}
                    </div>
                ) : (
                    <div className="space-y-6">
                        {groups.length > 0 && (
                            <div className="space-y-3">
                                <h3 className="text-sm font-semibold text-muted-foreground">Shipments</h3>
                                <div className="space-y-2">
                                    {groups.map(g => (
                                        <ContainerTile
                                            key={g.key}
                                            container={g}
                                            filtersActive={filtersActive}
                                            isExpanded={manualExpanded.has(g.key) || searchActive}
                                            onToggle={() => toggleGroup(g.key)}
                                            onRowClick={handleRowClick}
                                            onAssignPartner={handleAssignPartner}
                                            onReassignPartner={handleReassignPartner}
                                            onUnassignPartner={handleUnassignPartner}
                                            onArchiveVehicle={handleArchiveVehicle}
                                            onUnarchiveVehicle={handleUnarchiveVehicle}
                                            onOpenCustoms={handleOpenCustoms}
                                            customsEligible={customsEligibleClusterKeys.has(g.key)}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {notYetShipped.length > 0 && (
                            <NotYetShippedSection
                                vehicles={notYetShipped}
                                onRowClick={handleRowClick}
                                onAssignPartner={handleAssignPartner}
                                onReassignPartner={handleReassignPartner}
                                onUnassignPartner={handleUnassignPartner}
                                onArchiveVehicle={handleArchiveVehicle}
                                onUnarchiveVehicle={handleUnarchiveVehicle}
                            />
                        )}
                    </div>
                )}
            </CardContent>

            {/* Assign/Reassign Partner Sheet */}
            <AssignPartnerSheet
                open={assignPartnerOpen}
                onOpenChange={handlePartnerSheetClose}
                vehicleId={partnerAssignTarget?.id ?? null}
                vehicleLabel={partnerAssignTarget?.label ?? ''}
                currentPartnerId={partnerAssignTarget?.currentPartnerId ?? null}
            />

            {/* Unassign Partner Confirmation */}
            <AlertDialog open={!!unassignPartnerTarget} onOpenChange={(open) => !open && setUnassignPartnerTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remove Partner?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will remove <strong>{unassignPartnerTarget?.partnerName}</strong> from this vehicle.
                            The vehicle will appear as unassigned.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleConfirmUnassign}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Remove
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Archive Vehicle Confirmation */}
            <AlertDialog open={!!archiveTarget} onOpenChange={(open) => !open && setArchiveTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Archive vehicle?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This marks <strong>{archiveTarget?.label}</strong> as archived — for vehicles that won't
                            reach a contract (sold to a third party, procured for a third party, or kept for internal
                            Primelane use). You can undo this anytime via Unarchive.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleConfirmArchive}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Archive
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Customs (Bill of Entry / Tax Bill / Port Charges) Sheet */}
            <ContainerCustomsSheet
                container={customsTarget}
                open={!!customsTarget}
                onOpenChange={(open) => { if (!open) setCustomsTarget(null); }}
            />
        </Card>
    );
}
